import { useCallback, useEffect, useRef, useState } from "react";
import { ConstellationAPI } from "../infrastructure/api/Constellation.api";
import {
    BoardState,
    ConstellationBoard,
    isMultiWriterBoard,
    PublicBoard,
    SessionType,
    ShareLink,
    TherapistParticipantPresence,
} from "../domain/types";

const PARTICIPANT_ID_STORAGE_PREFIX = "constellation:participant:";

function getParticipantStorageKey(token: string): string {
    return `${PARTICIPANT_ID_STORAGE_PREFIX}${token}`;
}

type LoadStatus = "idle" | "loading" | "ready" | "error";

const THERAPIST_POLL_MS = 1000;
const PATIENT_POLL_MS = 1000;
// Edits are sent at most this often while they keep coming (a drag streams
// instead of waiting for the hand to stop), and never two at once.
const AUTOSAVE_MS = 300;
// How far a throttled (429) poll or save backs off, at most.
const MAX_BACKOFF_MS = 8000;

function errorStatus(err: unknown): number | undefined {
    return (err as { response?: { status?: number } })?.response?.status;
}

function errorDetail(err: unknown): string | undefined {
    return (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
}

/** The server refused a write made against a version that is no longer current. */
function isStaleVersion(err: unknown): boolean {
    return errorStatus(err) === 400 && !!errorDetail(err)?.toLowerCase().includes("stale");
}

/**
 * Run `tick` every `everyMs`, one at a time: the next waits for the last to
 * answer, so a slow network queues nothing and answers never arrive out of
 * order. A tick that reports it was throttled doubles the wait, up to
 * MAX_BACKOFF_MS. Returns the stop function.
 */
function startPolling(tick: () => Promise<"ok" | "throttled">, everyMs: number): () => void {
    let stopped = false;
    let delay = everyMs;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const run = async () => {
        const outcome = await tick();
        if (stopped) return;
        delay = outcome === "throttled" ? Math.min(delay * 2, MAX_BACKOFF_MS) : everyMs;
        timer = setTimeout(run, delay);
    };
    timer = setTimeout(run, everyMs);
    return () => {
        stopped = true;
        if (timer) clearTimeout(timer);
    };
}

/**
 * Therapist-side board hook. Polls the authenticated board endpoint so the
 * therapist view reflects the patient's edits within a bounded delay.
 */
export type SaveStatus = "idle" | "saving" | "saved" | "error";

export function useTherapistBoard(boardId: string | undefined) {
    const [board, setBoard] = useState<ConstellationBoard | null>(null);
    const [status, setStatus] = useState<LoadStatus>("idle");
    const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
    const [shareLink, setShareLink] = useState<ShareLink | null>(null);
    const [participants, setParticipants] = useState<TherapistParticipantPresence[]>([]);
    const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    // last board version we know about (own saves or hydrated remote edits);
    // start at -1 so the first load always hydrates.
    const versionRef = useRef<number>(-1);
    // Multi-participant boards (couple/anonymous) have several concurrent
    // writers; optimistic locking would reject every racing write and nothing
    // would sync. For those, write last-wins (no expected_version). Individual
    // boards keep optimistic locking (single therapist + single patient).
    const multiWriterRef = useRef<boolean>(false);
    // true from the first unsaved edit until it is persisted, so the poll
    // does not hydrate (and clobber) an edit we have not saved yet.
    const pendingRef = useRef<boolean>(false);
    const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    // The newest edit not yet sent, and whether a save is on the wire.
    const queuedRef = useRef<BoardState | null>(null);
    const inFlightRef = useRef<boolean>(false);
    // Bumped on every edit: a poll answered after an edit was made describes
    // the board from before it, and is dropped.
    const editSeqRef = useRef<number>(0);
    const loadedRef = useRef<boolean>(false);
    const participantsKeyRef = useRef<string>("");

    const refresh = useCallback(async (): Promise<"ok" | "throttled"> => {
        if (!boardId) return "ok";
        if (pendingRef.current) return "ok";
        const seq = editSeqRef.current;
        try {
            const { data } = await ConstellationAPI.getBoard(boardId);
            multiWriterRef.current = isMultiWriterBoard(data.session_type);
            const key = JSON.stringify(data.participants ?? []);
            if (key !== participantsKeyRef.current) {
                participantsKeyRef.current = key;
                setParticipants(data.participants ?? []);
            }
            // Hand the page a board only when it carries something newer than
            // what it shows: a new object on every poll re-renders the whole
            // board, and one from before our own edit would put it back.
            const stale =
                pendingRef.current ||
                seq !== editSeqRef.current ||
                (loadedRef.current && data.version <= versionRef.current);
            if (!stale) setBoard(data);
            loadedRef.current = true;
            setStatus("ready");
            return "ok";
        } catch (err) {
            // A board already on screen stays up through a dropped poll.
            if (!loadedRef.current) setStatus("error");
            return errorStatus(err) === 429 ? "throttled" : "ok";
        }
    }, [boardId]);

    useEffect(() => {
        if (!boardId) return;
        setStatus("loading");
        loadedRef.current = false;
        void refresh();
        return startPolling(refresh, THERAPIST_POLL_MS);
    }, [boardId, refresh]);

    const createLink = useCallback(async () => {
        if (!boardId) return;
        const { data } = await ConstellationAPI.createShareLink(boardId);
        setShareLink(data);
        return data;
    }, [boardId]);

    const revokeLink = useCallback(async () => {
        if (!boardId) return;
        await ConstellationAPI.revokeShareLink(boardId);
        setShareLink(null);
    }, [boardId]);

    const uploadSnapshot = useCallback(
        async (blob: Blob) => {
            if (!boardId) return;
            const { data } = await ConstellationAPI.uploadSnapshot(boardId, blob);
            setBoard(data);
        },
        [boardId],
    );

    const renameSnapshot = useCallback(
        async (index: number, name: string) => {
            if (!boardId) return;
            const { data } = await ConstellationAPI.renameSnapshot(boardId, index, name);
            setBoard(data);
        },
        [boardId],
    );

    const deleteSnapshot = useCallback(
        async (index: number) => {
            if (!boardId) return;
            const { data } = await ConstellationAPI.deleteSnapshot(boardId, index);
            setBoard(data);
        },
        [boardId],
    );

    const close = useCallback(
        async (notes: string, saveScene?: { name: string; description?: string }) => {
            if (!boardId) return;
            await ConstellationAPI.close(boardId, notes, saveScene);
        },
        [boardId],
    );

    // Therapist edits: throttled authed save, one at a time, always the
    // newest state. Updates versionRef so the poll does not re-hydrate (and
    // clobber) our own change.
    const flushSave = useCallback(async () => {
        saveTimer.current = null;
        const state = queuedRef.current;
        queuedRef.current = null;
        if (!boardId || !state) {
            pendingRef.current = false;
            return;
        }
        inFlightRef.current = true;
        let retryIn = AUTOSAVE_MS;
        try {
            const expected =
                multiWriterRef.current || versionRef.current < 0
                    ? undefined
                    : versionRef.current;
            const { data } = await ConstellationAPI.updateBoardState(boardId, state, expected);
            versionRef.current = data.version;
            setSaveStatus("saved");
            if (savedTimer.current) clearTimeout(savedTimer.current);
            savedTimer.current = setTimeout(() => setSaveStatus("idle"), 1500);
        } catch (err) {
            if (errorStatus(err) === 429) {
                // Throttled: keep the edit and send it again a little later.
                queuedRef.current ??= state;
                retryIn = 1000;
            } else if (isStaleVersion(err)) {
                // The patient wrote first. Their board wins; let the next poll
                // bring it in instead of leaving ours stuck unsaved.
                queuedRef.current = null;
                setSaveStatus("idle");
            } else {
                setSaveStatus("error");
                // ignore; next poll reconciles
            }
        } finally {
            inFlightRef.current = false;
            if (queuedRef.current) {
                saveTimer.current = setTimeout(() => void flushSave(), retryIn);
            } else {
                pendingRef.current = false;
            }
        }
    }, [boardId]);

    const saveState = useCallback(
        (state: BoardState) => {
            if (!boardId) return;
            queuedRef.current = state;
            editSeqRef.current += 1;
            pendingRef.current = true;
            setSaveStatus("saving");
            if (!saveTimer.current && !inFlightRef.current) {
                saveTimer.current = setTimeout(() => void flushSave(), AUTOSAVE_MS);
            }
        },
        [boardId, flushSave],
    );

    useEffect(
        () => () => {
            if (saveTimer.current) clearTimeout(saveTimer.current);
            if (savedTimer.current) clearTimeout(savedTimer.current);
        },
        [],
    );

    const kickParticipant = useCallback(
        async (participantId: string) => {
            if (!boardId) return;
            await ConstellationAPI.kickParticipant(boardId, participantId);
            setParticipants((prev) => prev.filter((p) => p.participant_id !== participantId));
        },
        [boardId],
    );

    return {
        board,
        status,
        saveStatus,
        shareLink,
        versionRef,
        refresh,
        createLink,
        revokeLink,
        uploadSnapshot,
        renameSnapshot,
        deleteSnapshot,
        saveState,
        close,
        participants,
        kickParticipant,
    };
}

type TokenError = "expired" | "finalized" | "removed" | "network" | null;

/**
 * Patient-side board hook (public token). Loads the board and autosaves local
 * state mutations, throttled. Surfaces token errors explicitly.
 *
 * For couple/anonymous boards, exposes sessionType + participantId so the page
 * can gate the join flow before starting the poll loop.
 */
export function usePatientBoard(token: string | undefined) {
    const [data, setData] = useState<PublicBoard | null>(null);
    const [status, setStatus] = useState<LoadStatus>("idle");
    const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
    const [tokenError, setTokenError] = useState<TokenError>(null);
    const [sessionType, setSessionType] = useState<SessionType | null>(null);
    const [participantIdState, setParticipantIdState] = useState<string | null>(null);
    // Set when a save is rejected because the participant tried to change a
    // figure they do not own; the UI shows a transient message.
    const [ownershipError, setOwnershipError] = useState<boolean>(false);
    const versionRef = useRef<number>(0);
    // true from the first unsaved edit until it is persisted, so the poll
    // does not hydrate (and clobber) an edit we have not saved yet.
    const pendingRef = useRef<boolean>(false);
    const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    // The newest edit not yet sent, whether a save is on the wire, and a
    // counter bumped per edit so a poll answered after one is dropped.
    const queuedRef = useRef<BoardState | null>(null);
    const inFlightRef = useRef<boolean>(false);
    const editSeqRef = useRef<number>(0);
    // Couple/anonymous boards have multiple concurrent writers; write last-wins
    // (no expected_version) so racing edits are not rejected. See useTherapistBoard.
    const multiWriterRef = useRef<boolean>(false);

    // On mount: probe once (no participant_id) to discover session_type.
    // For individual boards, also seeds the initial board data.
    // For couple/anonymous boards, the full load waits until participantId is set.
    useEffect(() => {
        if (!token) return;
        let active = true;
        const storedId = sessionStorage.getItem(getParticipantStorageKey(token));
        if (storedId) setParticipantIdState(storedId);
        setStatus("loading");
        ConstellationAPI.getPublicBoard(token)
            .then(({ data: board }) => {
                if (!active) return;
                setSessionType(board.session_type ?? "individual");
                multiWriterRef.current = isMultiWriterBoard(board.session_type);
                if (board.session_type === "individual" || !board.session_type) {
                    setData(board);
                    versionRef.current = board.version;
                    setStatus("ready");
                } else {
                    // couple/anonymous: board loaded after join
                    setStatus("idle");
                }
            })
            .catch((err) => {
                if (!active) return;
                setStatus("error");
                const code = err?.response?.status;
                setTokenError(
                    code === 410 ? "finalized" : code === 404 ? "expired" : "network",
                );
            });
        return () => {
            active = false;
        };
    }, [token]);

    // Full board load after participantId becomes known (couple/anonymous flow).
    useEffect(() => {
        if (!token || !participantIdState) return;
        if (sessionType === "individual" || sessionType === null) return;
        let active = true;
        setStatus("loading");
        ConstellationAPI.getPublicBoard(token, participantIdState)
            .then(({ data: board }) => {
                if (!active) return;
                setData(board);
                versionRef.current = board.version;
                setStatus("ready");
            })
            .catch((err) => {
                if (!active) return;
                setStatus("error");
                const code = err?.response?.status;
                const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
                if (code === 410 && detail?.toLowerCase().includes("removed")) {
                    setTokenError("removed");
                } else {
                    setTokenError(code === 410 ? "finalized" : code === 404 ? "expired" : "network");
                }
            });
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, participantIdState, sessionType]);

    // Poll for remote (therapist) edits and hydrate only when the server has a
    // strictly newer version, so the patient sees the therapist's changes within
    // a bounded delay. Skipped while a local save is pending to avoid clobbering
    // unsaved edits. For couple/anonymous boards, only polls after participantId is set.
    useEffect(() => {
        if (!token) return;
        if (sessionType !== "individual" && sessionType !== null && !participantIdState) return;
        const poll = async (): Promise<"ok" | "throttled"> => {
            if (pendingRef.current) return "ok";
            const seq = editSeqRef.current;
            try {
                const { data: board } = await ConstellationAPI.getPublicBoard(
                    token,
                    participantIdState ?? undefined,
                );
                if (pendingRef.current || seq !== editSeqRef.current) return "ok";
                if (board.version > versionRef.current) {
                    versionRef.current = board.version;
                    setData(board);
                }
                return "ok";
            } catch (err: unknown) {
                const code = errorStatus(err);
                const detail = errorDetail(err);
                if (code === 429) return "throttled";
                if (code === 410) {
                    setStatus("error");
                    if (detail?.toLowerCase().includes("removed")) {
                        setTokenError("removed");
                    } else {
                        setTokenError("finalized");
                    }
                } else if (code === 404) {
                    setStatus("error");
                    setTokenError("expired");
                }
                // other (network) errors: ignore; next poll retries
                return "ok";
            }
        };
        return startPolling(poll, PATIENT_POLL_MS);
    }, [token, sessionType, participantIdState]);

    // Throttled public save, one at a time, always the newest state.
    const resync = useCallback(async () => {
        if (!token) return;
        try {
            const { data: board } = await ConstellationAPI.getPublicBoard(
                token,
                participantIdState ?? undefined,
            );
            versionRef.current = board.version;
            setData(board);
        } catch {
            // next poll retries
        }
    }, [token, participantIdState]);

    const flushSave = useCallback(async () => {
        saveTimer.current = null;
        const state = queuedRef.current;
        queuedRef.current = null;
        if (!token || !state) {
            pendingRef.current = false;
            return;
        }
        inFlightRef.current = true;
        let retryIn = AUTOSAVE_MS;
        try {
            const { data: board } = await ConstellationAPI.updatePublicState(
                token,
                state,
                multiWriterRef.current ? undefined : versionRef.current,
                participantIdState ?? undefined,
            );
            // Track the new version only. Do NOT setData(board) here: the
            // page hydrates (and resets undo/redo history) whenever `data`
            // changes, so echoing each save back would wipe the history.
            versionRef.current = board.version;
            setSaveStatus("saved");
            if (savedTimer.current) clearTimeout(savedTimer.current);
            savedTimer.current = setTimeout(() => setSaveStatus("idle"), 1500);
        } catch (err: unknown) {
            const status = errorStatus(err);
            const detail = errorDetail(err);
            if (status === 429) {
                // Throttled: keep the edit and send it again a little later.
                queuedRef.current ??= state;
                retryIn = 1000;
            } else if (status === 404) {
                setTokenError("expired");
                setSaveStatus("idle");
            } else if (status === 410) {
                // Kicked (or finalized) mid-edit: stop and surface it.
                setStatus("error");
                setTokenError(detail?.toLowerCase().includes("removed") ? "removed" : "finalized");
                setSaveStatus("idle");
            } else if (status === 403) {
                // Ownership: the participant changed a figure they do not
                // own. Discard the optimistic edit and resync to the
                // authoritative board, then surface a message.
                setSaveStatus("idle");
                setOwnershipError(true);
                queuedRef.current = null;
                await resync();
            } else if (isStaleVersion(err)) {
                // The therapist wrote first (seeding the board's style as it
                // opens, say). Take their board now rather than leaving this
                // edit failing until the next poll happens to replace it.
                setSaveStatus("idle");
                queuedRef.current = null;
                await resync();
            } else {
                // Transient network issue — keep the patient's work, show retrying.
                setSaveStatus("error");
            }
        } finally {
            inFlightRef.current = false;
            if (queuedRef.current) {
                saveTimer.current = setTimeout(() => void flushSave(), retryIn);
            } else {
                pendingRef.current = false;
            }
        }
    }, [token, participantIdState, resync]);

    const save = useCallback(
        (state: BoardState) => {
            if (!token) return;
            queuedRef.current = state;
            editSeqRef.current += 1;
            pendingRef.current = true;
            setSaveStatus("saving");
            if (!saveTimer.current && !inFlightRef.current) {
                saveTimer.current = setTimeout(() => void flushSave(), AUTOSAVE_MS);
            }
        },
        [token, flushSave],
    );

    useEffect(
        () => () => {
            if (saveTimer.current) clearTimeout(saveTimer.current);
            if (savedTimer.current) clearTimeout(savedTimer.current);
        },
        [],
    );

    const setParticipantId = useCallback(
        (id: string) => {
            if (!token) return;
            sessionStorage.setItem(getParticipantStorageKey(token), id);
            setParticipantIdState(id);
        },
        [token],
    );

    return {
        data,
        status,
        saveStatus,
        tokenError,
        save,
        sessionType,
        participantId: participantIdState,
        setParticipantId,
        ownershipError,
        clearOwnershipError: () => setOwnershipError(false),
    };
}
